import React, { useEffect, useMemo, useState } from "react";
import { Bell, CheckCheck, Clock3, MessageSquare, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const RELEASE_ID = "release-notifications-center-2026-08";

function trialDeadline(user) {
  return user?.trial_ends_at || user?.trial_end_at || user?.trial_expires_at || user?.trial_expiry || null;
}

function daysRemaining(value) {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  return Math.ceil((time - Date.now()) / 86400000);
}

export default function NotificationBell({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState([]);

  const storageKey = user?.id ? "ttt-notifications-read-" + user.id : "ttt-notifications-read-guest";

  useEffect(() => {
    try {
      setReadIds(JSON.parse(window.localStorage.getItem(storageKey) || "[]"));
    } catch {
      setReadIds([]);
    }
  }, [storageKey]);

  const notifications = useMemo(() => {
    const items = [{
      id: RELEASE_ID,
      icon: Sparkles,
      title: "A new patch is live",
      body: "The notification center is now available from the bell in the navigation.",
      action: () => navigate("/changelog"),
    }];

    const deadline = trialDeadline(user);
    const remaining = deadline ? daysRemaining(deadline) : null;
    if (remaining !== null && remaining >= 0 && remaining <= 7) {
      items.unshift({
        id: "trial-" + deadline,
        icon: Clock3,
        title: remaining === 0 ? "Your trial ends today" : "Your trial ends in " + remaining + " day" + (remaining === 1 ? "" : "s"),
        body: "Choose a plan to keep your saga, progress, and Codex ready for your next chapter.",
        action: () => navigate("/pricing"),
      });
    }

    const ticketUpdates = Number(user?.support_ticket_updates || user?.ticket_updates || 0);
    if (ticketUpdates > 0) {
      items.unshift({
        id: "tickets-" + ticketUpdates,
        icon: MessageSquare,
        title: ticketUpdates + " support update" + (ticketUpdates === 1 ? "" : "s"),
        body: "There is new activity on one of your contact or feedback tickets.",
        action: () => navigate("/support"),
      });
    }

    return items;
  }, [user, navigate]);

  const unreadCount = notifications.filter((item) => !readIds.includes(item.id)).length;

  const markAllRead = () => {
    const next = notifications.map((item) => item.id);
    setReadIds(next);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const openNotification = (item) => {
    const next = Array.from(new Set([...readIds, item.id]));
    setReadIds(next);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
    setOpen(false);
    item.action();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="relative grid h-9 w-9 place-items-center border border-amber-700/50 text-amber-200 transition hover:bg-amber-900/20"
        aria-label="Open notifications"
        data-testid="notifications-bell"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-stone-950">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-3 w-[min(24rem,calc(100vw-2rem))] border border-amber-700/60 bg-[#15121a] shadow-2xl" data-testid="notifications-overlay">
          <div className="flex items-center justify-between border-b border-amber-700/30 px-4 py-3">
            <div>
              <p className="font-display text-lg text-amber-100">Notifications</p>
              <p className="text-xs text-amber-100/55">{unreadCount ? unreadCount + " unread" : "You are caught up"}</p>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={markAllRead} className="grid h-8 w-8 place-items-center text-amber-200 hover:bg-amber-900/20" aria-label="Mark all notifications as read" title="Mark all as read">
                <CheckCheck className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setOpen(false)} className="grid h-8 w-8 place-items-center text-amber-200 hover:bg-amber-900/20" aria-label="Close notifications" title="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[min(28rem,calc(100vh-8rem))] overflow-y-auto">
            {notifications.map((item) => {
              const Icon = item.icon;
              const unread = !readIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openNotification(item)}
                  className="flex w-full gap-3 border-b border-amber-700/20 px-4 py-4 text-left transition hover:bg-amber-900/15"
                  data-testid={"notification-" + item.id}
                >
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center border border-amber-700/45 text-amber-300">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-amber-100">{item.title}</span>
                      {unread && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" aria-label="Unread" />}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-amber-100/65">{item.body}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
